import type { ChartSeriesInput, ChartType } from '#core/components/dashboard/panels/chart/schema.ts';
import type { DashboardGridData } from '#core/components/dashboard/schema.ts';
import { extractChartAxes } from '#core/lib/transforms/extract-chart-axes.ts';
import { extractNumericField } from '#core/lib/transforms/extract-numeric-field.ts';

import type { LlmChartSeries } from '#plugin-chat/components/tools/show-dashboard/schema.ts';
import { TIMESERIES_TIMESTAMP_COLUMN } from '#plugin-chat/lib/data-pipeline/steps/fetch-timeseries.ts';
import type { Dataset, DatasetRow } from '#plugin-chat/lib/data-pipeline/types.ts';

export interface TableAdapterOutput {
  records: readonly DatasetRow[];
  columns: Array<{ accessor: string; title: string }>;
}

export interface ChartAdapterOutput {
  data: readonly DatasetRow[];
  xAxis: string;
  yAxis: string;
  chartType?: ChartType;
  series?: ChartSeriesInput[];
  /**
   * Human-readable error set by the adapter when it detects a malformed
   * chart spec it cannot render (e.g. long-format data mistaken for wide
   * format, producing duplicate series column refs). When present,
   * `AdaptedChart` renders `<BlockError>` instead of the chart.
   */
  error?: string;
}

export interface KpiAdapterOutput {
  value: number | string;
  label: string;
  unit?: string;
}

/**
 * Safely extracts DashboardGridData from tool args.
 *
 * Tagged-object conversion (LLM format → discriminated format) is handled
 * generically by createTool. This function handles streaming-specific concerns:
 * partial data, incomplete items, and empty structures.
 *
 * Presentation fields (`caption`, `captionPosition`, `layer`) live at item
 * level on both the LLM input and core's `GridItemSchema` — no hoist required.
 */
export function extractDashboardData(args: unknown): DashboardGridData {
  // Handle undefined/null during streaming
  if (!args || typeof args !== 'object') {
    return { layout: { rows: 1, columns: 1 }, items: [] };
  }

  const gridData = args as DashboardGridData;

  // Check for valid structure (has items array)
  if (!('items' in gridData) || !Array.isArray(gridData.items)) {
    return { layout: { rows: 1, columns: 1 }, items: [] };
  }

  // Filter out incomplete items during streaming.
  // An item is incomplete if it lacks component.type (still being streamed).
  // This prevents flickering from rendering partial/changing items.
  const completeItems = gridData.items.filter(
    (item) => item.component != null && typeof item.component.type === 'string',
  );

  return { ...gridData, items: completeItems };
}

/**
 * Color palette for timeseries chart overlay comparison.
 * Uses distinct colors optimized for distinguishing multiple series.
 */
const TIMESERIES_COLORS = [
  'blue',
  'green',
  'orange',
  'red',
  'grape',
  'teal',
  'cyan',
  'lime',
  'pink',
  'yellow',
] as const;

/**
 * Formats a timeseries column name into a human-readable label.
 * Handles camelCase, snake_case, and colon-separated names.
 *
 * @example
 * formatSeriesLabel('priorityOilRateStd') // 'Priority Oil Rate Std'
 * formatSeriesLabel('ME010-S:WellOutputs:oil') // 'Oil'
 */
function formatSeriesLabel(name: string): string {
  // If it contains colons (CDF external ID format), extract and format last segment
  if (name.includes(':')) {
    const parts = name.split(':');
    const lastPart = parts.at(-1) ?? name;
    return lastPart
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());
  }
  // Handle camelCase and snake_case
  return name
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Transform a {@link Dataset} into table adapter output.
 *
 * Column definitions come directly from {@link Dataset.columns} — the dataset
 * already carries typed column metadata, so there is no need to re-derive
 * accessors from the first row's keys.
 *
 * @param dataset - Dataset resolved from an upstream tool's outputDataset
 * @returns Table adapter output with records and column definitions
 */
export function createTableAdapter(dataset: Dataset): TableAdapterOutput {
  const columns = dataset.columns.map((column) => ({ accessor: column.name, title: column.name }));
  return { records: dataset.rows, columns };
}

/**
 * Transform a {@link Dataset} into chart adapter output.
 *
 * Uses `extractChartAxes` to determine x/y axis fields from the dataset rows.
 *
 * @param dataset - Dataset resolved from an upstream tool's outputDataset
 * @param options - Optional chart configuration
 * @param options.chartType - Chart type (defaults to 'bar')
 * @returns Chart adapter output with data and axis configuration
 */
export function createChartAdapter(dataset: Dataset, options?: { chartType?: ChartType }): ChartAdapterOutput {
  const { xAxis, yAxis } = extractChartAxes(dataset.rows as Record<string, unknown>[]);

  return {
    data: dataset.rows,
    xAxis,
    yAxis,
    chartType: options?.chartType ?? 'bar',
  };
}

/**
 * Transform a {@link Dataset} into KPI adapter output.
 *
 * Extracts the first numeric field in the first row as the KPI value.
 *
 * @param dataset - Dataset resolved from an upstream tool's outputDataset
 * @returns KPI adapter output with value and label
 */
export function createKpiAdapter(dataset: Dataset): KpiAdapterOutput {
  const firstRecord = dataset.rows[0];

  if (!firstRecord) {
    return { value: 'N/A', label: 'Value' };
  }

  const numericResult = extractNumericField(firstRecord);

  return {
    value: numericResult?.value ?? 'N/A',
    label: numericResult?.field ?? 'Value',
  };
}

/**
 * Transform a timeseries {@link Dataset} into chart adapter output with
 * multi-series overlay support.
 *
 * Assumes a `timestamp` column plus one or more numeric series columns — the
 * canonical shape produced by `fetchTimeseriesStep`. Series column names
 * come from {@link Dataset.columns} rather than the first row's keys, so
 * empty datasets still yield the correct x-axis configuration.
 *
 * @param dataset - Dataset resolved from a `getDatapoints` outputDataset
 * @returns Chart adapter output with series configuration for overlay comparison
 */
export function createTimeseriesChartAdapter(dataset: Dataset): ChartAdapterOutput {
  const seriesColumns = dataset.columns
    .map((column) => column.name)
    .filter((name) => name !== TIMESERIES_TIMESTAMP_COLUMN);

  const series = seriesColumns.map((name, index) => ({
    name,
    label: formatSeriesLabel(name),
    color: TIMESERIES_COLORS[index % TIMESERIES_COLORS.length] ?? 'blue',
  }));

  return {
    data: dataset.rows,
    xAxis: TIMESERIES_TIMESTAMP_COLUMN,
    yAxis: seriesColumns[0] ?? 'value',
    chartType: 'line',
    series: series.length > 0 ? series : undefined,
  };
}

/**
 * Qualified column name: `${inputDataset}::${column}`. Used to disambiguate
 * same-named columns from different datasets when merging chart series.
 */
export function qualifyColumn(inputDataset: string, column: string): string {
  return `${inputDataset}::${column}`;
}

/**
 * Merge rows from multiple datasets on a shared `xAxis` column.
 *
 * Each dataset contributes its rows to the merged output, with every
 * non-xAxis column renamed to `${inputDataset}::${column}` so same-named
 * columns from different datasets stay distinct. Rows are keyed by
 * `xAxis` value; missing values remain `undefined` (Recharts renders
 * gaps natively).
 *
 * Rows are sorted by `xAxis` using ascending numeric/date order when
 * possible, otherwise string order.
 *
 * Used by `AdaptedChart` to translate the LLM-facing per-series
 * `inputDataset` + `column` form into core's classic flat-row shape.
 */
export function mergeDatasetsByXaxis(datasets: Map<string, Dataset | undefined>, xAxis: string): DatasetRow[] {
  const taggedRows = Array.from(datasets).flatMap(([inputDataset, dataset]) =>
    (dataset?.rows ?? []).map((row) => ({ inputDataset, row })),
  );

  const merged = taggedRows.reduce<Map<unknown, Record<string, unknown>>>((acc, { inputDataset, row }) => {
    const xValue = row[xAxis];
    if (xValue == null) {
      return acc;
    }
    const existing = acc.get(xValue) ?? { [xAxis]: xValue };
    const updated = Object.entries(row).reduce<Record<string, unknown>>((target, [col, val]) => {
      if (col !== xAxis) {
        target[qualifyColumn(inputDataset, col)] = val;
      }
      return target;
    }, existing);
    acc.set(xValue, updated);
    return acc;
  }, new Map());

  return Array.from(merged.values()).sort((a, b) => compareXaxisValues(a[xAxis], b[xAxis]));
}

function compareXaxisValues(a: unknown, b: unknown): number {
  if (typeof a === 'number' && typeof b === 'number') {
    return a - b;
  }
  if (a instanceof Date && b instanceof Date) {
    return a.getTime() - b.getTime();
  }
  return String(a).localeCompare(String(b));
}

/**
 * Extract the LLM-shape series array from a chart component, if any.
 *
 * Returns `[]` when the component's series use core's classic `name`-based
 * shape (or no series at all) — in that case the single-dataset path via
 * `item.inputDataset` handles rendering.
 */
export function extractLlmSeries(component: Record<string, unknown>): LlmChartSeries[] {
  const raw = component.series;
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.filter((s): s is LlmChartSeries => {
    return typeof s === 'object' && s !== null && typeof (s as { inputDataset?: unknown }).inputDataset === 'string';
  });
}

/**
 * Build a chart output from per-series `inputDataset` + `column` references.
 *
 * Two modes:
 * 1. **Single-source** (all series share one `inputDataset`) — the dataset's
 *    rows are passed through unchanged and each series' `name` is set to
 *    its raw `column`. No xAxis join is performed, so non-cartesian chart
 *    types (radar, pie, scatter, bubble, …) work out of the box.
 * 2. **Multi-source** (two or more distinct `inputDataset`s) — rows from each
 *    dataset are joined on the shared `xAxis` column, with non-xAxis
 *    columns renamed to `${inputDataset}::${column}` to avoid collisions.
 *    Each series' `name` is rewritten to the qualified form. This is the
 *    overlay case (e.g. daily + weekly-averaged).
 */
export function buildMergedChartOutput(
  llmSeries: readonly LlmChartSeries[],
  datasets: Map<string, Dataset | undefined>,
  component: Record<string, unknown>,
): ChartAdapterOutput {
  const xAxis = typeof component.xAxis === 'string' ? component.xAxis : 'timestamp';
  const inputDatasets = new Set(llmSeries.map((s) => s.inputDataset));
  const isSingleSource = inputDatasets.size === 1;

  // Datapoint-array columns are no longer auto-flattened here — the pipeline's
  // `expandTimeseries` action is now the single source of truth for reshaping
  // datapoint arrays into wide-format chart input. Series targeting an array
  // column will fail validation upstream with a guidance to call
  // `expandTimeseries` first.

  // Compute the final chart-series name for each LLM series once. In the
  // single-source path the name is the raw `column`; in the multi-source
  // path it is qualified with `inputDataset` to disambiguate same-named
  // columns across datasets. Duplicate names at this stage produce a
  // React duplicate-key warning and a silently bad render — e.g.
  // long-format data (one row per (x, group) with a discriminator
  // column) where multiple series end up pointing at the same `column`.
  // Execute-time validation (`validateDashboardSpec`) rejects this before
  // the dashboard reaches render on a fresh turn; this branch is the
  // belt-and-suspenders defense for post-reload recovery drift.
  const seriesNames = llmSeries.map((s) => (isSingleSource ? s.column : qualifyColumn(s.inputDataset, s.column)));
  const duplicates = findDuplicateSeriesNames(llmSeries);
  if (duplicates.length > 0) {
    return {
      data: [],
      xAxis,
      yAxis: '',
      error: `Chart ${formatDuplicateSeriesMessage(duplicates)}`,
    };
  }

  const series: ChartSeriesInput[] = llmSeries.map((s, i) => ({
    name: seriesNames[i] ?? s.column,
    label: s.label,
    color: s.color,
    shade: s.shade,
    type: s.type,
    lineStyle: s.lineStyle,
  }));

  const data = isSingleSource
    ? (datasets.get(llmSeries[0]?.inputDataset ?? '')?.rows ?? [])
    : mergeDatasetsByXaxis(datasets, xAxis);

  return {
    data,
    xAxis,
    yAxis: series[0]?.name ?? 'value',
    chartType: 'line',
    series,
  };
}

export interface ResolveChartOutputArgs {
  llmSeries: readonly LlmChartSeries[];
  multiDatasets: Map<string, Dataset | undefined>;
  singleDataset: Dataset | undefined;
  toolName: string | undefined;
  component: Record<string, unknown>;
  transformSingleDataset: (dataset: Dataset, toolName: string | undefined) => ChartAdapterOutput | undefined;
}

/**
 * Pick the right chart adapter output based on item shape: merged per-series
 * path when any series has a `inputDataset`, otherwise the classic
 * single-dataset path driven by item-level `inputDataset`.
 */
export function resolveChartOutput(args: ResolveChartOutputArgs): ChartAdapterOutput | undefined {
  if (args.llmSeries.length > 0) {
    return buildMergedChartOutput(args.llmSeries, args.multiDatasets, args.component);
  }
  if (args.singleDataset) {
    return args.transformSingleDataset(args.singleDataset, args.toolName);
  }
  return undefined;
}
/**
 * Compute duplicate chart series column names (for duplicate-(inputDataset,
 * column) detection).
 *
 * Returns the display names that appear more than once, using the same
 * naming scheme as {@link buildMergedChartOutput}:
 * - Single-source charts use the bare `column`.
 * - Multi-source charts use the `${inputDataset}::${column}` qualified form.
 *
 * Shared between execute-time {@link validateDashboardSpec} and
 * render-time `buildMergedChartOutput` so both paths flag the same
 * malformed specs consistently.
 */
export function findDuplicateSeriesNames(llmSeries: readonly LlmChartSeries[]): string[] {
  if (llmSeries.length === 0) {
    return [];
  }
  const isSingleSource = new Set(llmSeries.map((s) => s.inputDataset)).size === 1;
  const counts = llmSeries.reduce<Map<string, number>>((acc, s) => {
    const name = isSingleSource ? s.column : qualifyColumn(s.inputDataset, s.column);
    return acc.set(name, (acc.get(name) ?? 0) + 1);
  }, new Map());
  return Array.from(counts)
    .filter(([, count]) => count > 1)
    .map(([name]) => name);
}

/**
 * Format the human-readable error shown when a chart spec targets the same
 * `(inputDataset, column)` across multiple series. Emitted by both the
 * execute-time validator and the render-side adapter so the two surfaces
 * tell the LLM / user the same story.
 */
export function formatDuplicateSeriesMessage(duplicates: readonly string[]): string {
  return `series reference the same (inputDataset, column) multiple times (${duplicates
    .map((n) => `"${n}"`)
    .join(
      ', ',
    )}). Each series must map to a distinct column. Two recovery paths: (a) if your dataset has multiple rows holding datapoint-array columns (e.g. one row per well with a \`daily(...)\` column), end your transformDataset pipeline with an \`expandTimeseries\` action — \`{ type: 'expandTimeseries', columns: ['<arrCol>'], splitBy: '<id-column>' }\` — to turn each row into its own per-series column; (b) if your dataset is in long format (one row per (x, group) with a discriminator column), reshape it to wide format with a \`pivot\` action on the discriminator. Then point each chart series at one of the resulting per-group columns.`;
}
